import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import FormField from "@mhj/shared/FormField";
import { apiRequest } from "@mhj/shared/api";
import { useAuth } from "@mhj/shared/auth";

function useQueryParam(name: string) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  return params.get(name);
}

export default function VerifyEmail() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const tokenFromUrl = useQueryParam("token") ?? "";
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<{
    token: string;
  }>({
    defaultValues: {
      token: tokenFromUrl,
    },
  });
  const verifyMutation = useMutation({
    mutationFn: (payload: { token: string }) =>
      apiRequest("/auth/verify-email", {
        method: "POST",
        data: payload,
      }),
    onSuccess: async () => {
      await refresh();
      toast.success("Email verified. You can now continue your application.");
      navigate("/portal", { replace: true });
    },
  });

  useEffect(() => {
    setValue("token", tokenFromUrl);
  }, [tokenFromUrl, setValue]);

  const onSubmit = async (values: { token: string }) => {
    try {
      await verifyMutation.mutateAsync({ token: values.token });
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white/90 px-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="glass-card w-full max-w-md rounded-3xl p-8"
      >
        <p className="text-xs uppercase tracking-[0.3em] text-blue-300">
          Verify email
        </p>
        <h1 className="mt-3 text-2xl font-semibold text-black">
          Confirm your account
        </h1>
        <p className="mt-2 text-sm text-black/60">
          Paste the verification token from your email.
        </p>
        <div className="mt-6">
          <FormField label="Verification token">
            <input
              type="text"
              {...register("token", { required: "Token is required" })}
              className="rounded-2xl border border-black/10 bg-white/60 px-4 py-3 text-black placeholder:text-black/30 focus:border-blue-400 focus:outline-none"
              placeholder="Paste token"
            />
          </FormField>
        </div>
        {errors.token ? (
          <p className="mt-3 text-xs text-red-500">{errors.token.message}</p>
        ) : null}
        <button
          type="submit"
          disabled={verifyMutation.isPending}
          className="mt-6 w-full rounded-full bg-blue-500 px-4 py-3 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-blue-400 disabled:cursor-not-allowed disabled:bg-white/20"
        >
          {verifyMutation.isPending ? "Verifying..." : "Verify email"}
        </button>
      </form>
    </div>
  );
}
